import {
  PropsWithChildren,
  ReactElement,
  createContext,
  useCallback,
  useContext,
} from 'react';

const DefaultFeaturesConfig = {
  'core.auth': undefined,
  'flagsPage.title': undefined,
  'flagsPage.permissions': undefined,
  'flagsPage.supportSnippet': undefined,
  'flagPage.stickyAssignments': undefined,
  'flagPage.exclusionCriteria': undefined,
  'clientsPage.breadcrumbs': undefined,
  'clientsPage.permissions': undefined,
  'permission.button': undefined,
  'links.docs': undefined,
  'links.entity': undefined,
  'styling.dialogTitle': undefined,
  'styling.link': undefined,
  'styling.textField': undefined,
  'segment.targetingCriteria': undefined,
} as const;

export type FeatureId = keyof typeof DefaultFeaturesConfig;

type Feature =
  | undefined
  | boolean
  | number
  | string
  | null
  | ReactElement
  | Function;

export type AltFeaturesConfig = Partial<Record<FeatureId, Feature>>;

type AltFeaturesContextState = {
  features: AltFeaturesConfig;
  getFeature: (name: FeatureId) => Feature;
};

const AltFeaturesContext = createContext<AltFeaturesContextState>({
  features: DefaultFeaturesConfig,
  getFeature: name => DefaultFeaturesConfig[name],
});

export const AltFeaturesProvider = ({
  children,
  features = DefaultFeaturesConfig,
}: PropsWithChildren<Partial<AltFeaturesContextState>>) => {
  const getFeature = useCallback(
    (name: FeatureId) =>
      typeof features[name] !== 'undefined'
        ? features[name]
        : DefaultFeaturesConfig[name],
    [features],
  );

  const state = {
    features,
    getFeature,
  };

  return (
    <AltFeaturesContext.Provider value={state}>
      {children}
    </AltFeaturesContext.Provider>
  );
};

export const useAltFeaturesContext = () => useContext(AltFeaturesContext);

export const useAltFeature = (
  name?: FeatureId,
  getDefaultFeature?: () => unknown,
) => {
  const { getFeature } = useAltFeaturesContext();

  if (!name) return getDefaultFeature?.();

  const feature = getFeature(name);

  if (feature === undefined && getDefaultFeature?.() !== undefined) {
    return getDefaultFeature();
  }

  return typeof feature === 'function' ? feature() : feature;
};

export const AltFeature = ({
  name,
  children,
}: PropsWithChildren<{ name: FeatureId }>) => {
  const feature = useAltFeature(name);

  if (feature === undefined) {
    return children;
  }

  return <>{feature}</>;
};
